import React from 'react'
import { useTypedSelector } from 'src/State/RootReducer'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useShopCart } from 'src/Libs/shop/hooks'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { ProductGeneralType, ProductType, ProgressBarSteps } from 'src/Libs/shop/types'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import useModals from 'src/Hooks/useModals'
import { SYSTEM_CONFIGURE_PROGRESS_PAGE_PARAM } from 'src/Libs/shop/consts'
import { useDispatch } from 'react-redux'
import { ShopCheckoutSideBarResponse } from 'src/Libs/shop/models/shop-checkout-side-bar.model'
import { appUpdate } from '@e3dc-react/shell/State/App/AppActions'
import { BottomProgressBar } from '../../../BottomProgressBar'
import { resetConfigurator } from 'src/State/ShopQntmSystemConfiguration/ShopQntmSystemConfigurationAction'

interface QntmBottomProgressBarProps {
  handleBack?: boolean
  progress: number
  handleProgress?: (progress: ProgressBarSteps) => void
  allProductsSelected?: boolean
}

/*
 * Handles bottom progress actions and cart flow for normal system configuration
 */
const QntmBottomProgressBar: React.FC<QntmBottomProgressBarProps> = ({ handleBack, progress, handleProgress, allProductsSelected }) => {
  const configuratorItems = useTypedSelector(state => state.qntmStorage.items)
  const configuratorId = configuratorItems.length > 0 ? configuratorItems[0].configuration.configuratorId : null
  const systemConfiguration = configuratorItems.length > 0 ? configuratorItems[0] : null

  const { addSystemConfigurationCart, getCheckoutProducts, deleteProductCart, checkHasShopCartErrors, updateSystemConfigurationCart } = useShopCart()

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()
  const { t } = useCustomTranslation()
  const { openShopCheckoutSideBar, openShopLeavingConfirmation } = useModals()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const { configurationId } = useParams<{ configurationId?: string }>()

  const isEditSystem = Boolean(configurationId)

  const handleAddSystemConfiguratorToCart = async (): Promise<void> => {
    if (!systemConfiguration) return

    let result: ShopCheckoutSideBarResponse | null

    if (isEditSystem && configurationId) {
      result = await updateSystemConfigurationCart(configurationId, systemConfiguration)
    } else {
      result = await addSystemConfigurationCart(systemConfiguration)
    }

    if (result === null) {
      return enqueueErrorStack(t('shop.system-configurator.add-system-configure-to-basket-fail'))
    }

    if (result?.errors && Object.keys(result.errors).length > 0) {
      checkHasShopCartErrors(result)
    }

    enqueueSuccessStack(t('shop.progress-bar.added-sucessfully'))
    navigate('/shop/homepage?allow-redirect')
    openShopCheckoutSideBar()
    dispatch(resetConfigurator())
    dispatch(appUpdate({ moreActive: false }))
  }

  const handleAddOrEditSystemConfigurationToCart = async (): Promise<void> => {
    const result = await getCheckoutProducts()

    const hasNonQntm = (result?.lineItems ?? []).some(product => {
      const isQntmConfigurator = product.type === ProductGeneralType.QNTM_SYSTEM_CONFIGURATOR
      const isQntmProduct = product.type === ProductGeneralType.PRODUCT && product.payload?.e3dc?.productType === ProductType.QntmProduct

      return !(isQntmConfigurator || isQntmProduct)
    })
    const basketIds = hasNonQntm ? (result?.lineItems ?? []).map(product => product.id) : []

    if (hasNonQntm) {
      return openShopLeavingConfirmation({
        title: t('shop.system-configurator.modal-quantum-title'),
        text: t('shop.system-configurator.modal-quantum-text'),
        confirmButtonText: t('shop.common.confirm'),
        cancelButtonText: t('shop.common.cancel'),
        onConfirm: async onClose => {
          await deleteProductCart(basketIds)
          onClose()
          handleAddSystemConfiguratorToCart()
        },
      })
    }

    return handleAddSystemConfiguratorToCart()
  }

  const checkBasicSystemsOnCart = (): void => {
    navigate(`${location.pathname}?${SYSTEM_CONFIGURE_PROGRESS_PAGE_PARAM}=${ProgressBarSteps.MainComponent}`)
  }

  const handlePrimaryAction = async (): Promise<void> => {
    switch (progress) {
      case ProgressBarSteps.BasicSystem:
        checkBasicSystemsOnCart()
        break

      case ProgressBarSteps.MainComponent:
        navigate(location.pathname + `?${SYSTEM_CONFIGURE_PROGRESS_PAGE_PARAM}=${ProgressBarSteps.AdditionalComponent}`, { replace: true })
        break

      case ProgressBarSteps.AdditionalComponent:
        await handleAddOrEditSystemConfigurationToCart()
        break
    }
  }

  const primaryButtonLabel = progress <= ProgressBarSteps.MainComponent ? t('shop.common.continue') : t('shop.common.into-basket')

  return (
    <BottomProgressBar
      handleBack={handleBack}
      progress={progress}
      onStepChange={handleProgress}
      onPrimaryAction={handlePrimaryAction}
      allProductsSelected={allProductsSelected}
      configuratorId={configuratorId}
      primaryButtonLabel={primaryButtonLabel}
      showSaveConfigurator={false}
    />
  )
}

export default QntmBottomProgressBar
